// Mobile picker: tap-to-open bottom sheet with a sticky search input and
// filtered list. Designed for picking from per-account-configured lists
// (customers, products, locations) where the count can grow past what
// chip strips or native <select>s handle gracefully.
//
// SearchablePicker is single-select; MultiPicker is multi-select. Both
// share the same trigger field styling so they line up with the rest of
// the form selects, and both can hide the search bar via `searchable={false}`
// for short, predictable lists like stages or tags.

import { useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T } from "./theme"
import { BottomSheet, SearchBar, Touchable } from "./primitives"

export interface PickerOption {
  id: string | number
  name: string
}

interface PickerTriggerProps {
  /** Label shown when something is selected. Empty/null falls back to the placeholder. */
  selectedLabel: string | null
  placeholder?: string
  invalid?: boolean
  /** Optional element rendered before the label (e.g. a colored stage dot). */
  leading?: ReactNode
  onClick: () => void
}

/** Shared trigger field — looks like the styled <select> used elsewhere in the form. */
function PickerTrigger({
  selectedLabel,
  placeholder,
  invalid,
  leading,
  onClick,
}: PickerTriggerProps) {
  return (
    <Touchable
      onClick={onClick}
      style={{
        width: "100%",
        height: 44,
        padding: "0 12px",
        borderRadius: T.radius,
        border: `1px solid ${invalid ? T.danger : T.border}`,
        background: T.surface,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        fontFamily: T.fontBody,
        fontSize: 15,
        color: selectedLabel ? T.ink : T.inkSoft,
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          overflow: "hidden",
        }}
      >
        {leading}
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedLabel || (placeholder ?? "—")}
        </span>
      </span>
      <ChevronDown size={16} color={T.inkSoft} />
    </Touchable>
  )
}

// ─── Single-select ──────────────────────────────────────────────────────────

interface SearchablePickerProps {
  /** Currently selected id (stringified for compares); empty means "no selection". */
  value: string
  onChange: (id: string) => void
  options: PickerOption[]
  /** Header text on the bottom sheet. */
  title: string
  /** Placeholder when nothing is selected. */
  placeholder?: string
  /** When true, marks the trigger field with the danger border. */
  invalid?: boolean
  /** When true, shows a "Clear selection" row at the top of the list. */
  allowClear?: boolean
  /** Toggle the in-sheet search input. Defaults to `true`. */
  searchable?: boolean
  /** Override the default localized search placeholder. */
  searchPlaceholder?: string
  /** Optional element rendered at the start of each row (e.g. a colored dot). */
  renderLeading?: (option: PickerOption) => ReactNode
}

export function SearchablePicker({
  value,
  onChange,
  options,
  title,
  placeholder,
  invalid,
  allowClear,
  searchable = true,
  searchPlaceholder,
  renderLeading,
}: SearchablePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)) || null,
    [options, value],
  )

  // Reset the search box every time the sheet reopens so users start fresh.
  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const filtered = useMemo(() => {
    if (!searchable) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, query, searchable])

  const close = () => setOpen(false)

  const handleSelect = (id: string) => {
    onChange(id)
    close()
  }

  return (
    <>
      <PickerTrigger
        selectedLabel={selected?.name ?? null}
        placeholder={placeholder}
        invalid={invalid}
        leading={selected && renderLeading ? renderLeading(selected) : null}
        onClick={() => setOpen(true)}
      />

      <BottomSheet open={open} onClose={close} title={title}>
        {searchable && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              padding: "8px 16px 12px",
              background: T.surface,
              borderBottom: `1px solid ${T.borderSoft}`,
            }}
          >
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder ?? t("mobile.picker.searchPlaceholder")}
            />
          </div>
        )}

        <div style={{ padding: "4px 0 16px" }}>
          {allowClear && (
            <Touchable
              onClick={() => handleSelect("")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                color: T.inkSoft,
                borderBottom: `1px solid ${T.divider}`,
                fontSize: 14,
              }}
            >
              <span
                style={{
                  width: 18, height: 18,
                  display: "grid", placeItems: "center",
                }}
              >
                <X size={14} color={T.inkSoft} />
              </span>
              {t("mobile.picker.clearSelection")}
            </Touchable>
          )}

          {filtered.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: T.inkSoft,
                fontSize: 14,
              }}
            >
              {t("mobile.picker.noResults")}
            </div>
          ) : (
            filtered.map((o, i) => {
              const active = selected?.id === o.id
              return (
                <Touchable
                  key={o.id}
                  onClick={() => handleSelect(String(o.id))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    background: active ? T.surfaceAlt : "transparent",
                    borderBottom:
                      i === filtered.length - 1 ? "none" : `1px solid ${T.divider}`,
                  }}
                >
                  {renderLeading?.(o)}
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 15,
                      color: T.ink,
                      fontWeight: active ? 600 : 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.name}
                  </span>
                  {active && <Check size={16} color={T.accent} strokeWidth={2.5} />}
                </Touchable>
              )
            })
          )}
        </div>
      </BottomSheet>
    </>
  )
}

// ─── Multi-select ───────────────────────────────────────────────────────────

interface MultiPickerProps {
  /** Currently selected ids (stringified for compares). */
  value: string[]
  onChange: (ids: string[]) => void
  options: PickerOption[]
  title: string
  /** Placeholder when nothing is selected. */
  placeholder?: string
  invalid?: boolean
  /** Toggle the in-sheet search input. Defaults to `true`. */
  searchable?: boolean
  searchPlaceholder?: string
  renderLeading?: (option: PickerOption) => ReactNode
}

export function MultiPicker({
  value,
  onChange,
  options,
  title,
  placeholder,
  invalid,
  searchable = true,
  searchPlaceholder,
  renderLeading,
}: MultiPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const selectedSet = useMemo(() => new Set(value), [value])
  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(String(o.id))),
    [options, selectedSet],
  )

  // Trigger label: nothing → placeholder, one → its name, many → "N selected".
  const selectedLabel: string | null =
    selectedOptions.length === 0
      ? null
      : selectedOptions.length === 1
        ? selectedOptions[0].name
        : t("mobile.picker.selectedCount", { count: selectedOptions.length })

  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const filtered = useMemo(() => {
    if (!searchable) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, query, searchable])

  const toggle = (id: string) => {
    const next = new Set(selectedSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(Array.from(next))
  }

  const clearAll = () => onChange([])

  // For multi-select with a renderLeading, only show a leading on the trigger
  // when exactly one option is selected (otherwise the dot would be ambiguous).
  const triggerLeading =
    selectedOptions.length === 1 && renderLeading
      ? renderLeading(selectedOptions[0])
      : null

  return (
    <>
      <PickerTrigger
        selectedLabel={selectedLabel}
        placeholder={placeholder}
        invalid={invalid}
        leading={triggerLeading}
        onClick={() => setOpen(true)}
      />

      <BottomSheet open={open} onClose={() => setOpen(false)} title={title}>
        {searchable && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              padding: "8px 16px 12px",
              background: T.surface,
              borderBottom: `1px solid ${T.borderSoft}`,
            }}
          >
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder ?? t("mobile.picker.searchPlaceholder")}
            />
          </div>
        )}

        <div style={{ padding: "4px 0 16px" }}>
          {selectedSet.size > 0 && (
            <Touchable
              onClick={clearAll}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                color: T.inkSoft,
                borderBottom: `1px solid ${T.divider}`,
                fontSize: 14,
              }}
            >
              <span style={{ width: 18, height: 18, display: "grid", placeItems: "center" }}>
                <X size={14} color={T.inkSoft} />
              </span>
              {t("mobile.picker.clearSelection")}
            </Touchable>
          )}

          {filtered.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: T.inkSoft,
                fontSize: 14,
              }}
            >
              {t("mobile.picker.noResults")}
            </div>
          ) : (
            filtered.map((o, i) => {
              const id = String(o.id)
              const active = selectedSet.has(id)
              return (
                <Touchable
                  key={o.id}
                  onClick={() => toggle(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    background: active ? T.surfaceAlt : "transparent",
                    borderBottom:
                      i === filtered.length - 1 ? "none" : `1px solid ${T.divider}`,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 18, height: 18,
                      borderRadius: T.radius,
                      border: `1.5px solid ${active ? T.accent : T.border}`,
                      background: active ? T.accent : "transparent",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {active && <Check size={12} color="#fff" strokeWidth={3} />}
                  </span>
                  {renderLeading?.(o)}
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 15,
                      color: T.ink,
                      fontWeight: active ? 600 : 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.name}
                  </span>
                </Touchable>
              )
            })
          )}
        </div>
      </BottomSheet>
    </>
  )
}
